import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { signUpUser } from '../../services/sign_in_service';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import {
	Avatar,
    Typography,
	Button,
	CssBaseline,
	TextField,
	FormControlLabel,
	Checkbox,
	Grid,
	Box,
	Container
} from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { makeStyles } from '@material-ui/core/styles';
import Copyright from '../copyright/copyright';
import { useSnackbar } from 'notistack';
import { REACT_ROUTES } from '../../constants/react_routes';
import history from '../../services/history';

const useStyles = makeStyles((theme) => ({
	paper: {
		marginTop: theme.spacing(8),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center'
	},
	avatar: {
		margin: theme.spacing(1),
		backgroundColor: theme.palette.secondary.main
	},
	form: {
		width: '100%', // Fix IE 11 issue.
		marginTop: theme.spacing(1),
        '& > p': {
            color: '#bf1650',
            '&::before': {
                display: 'inline',
                content: '"⚠ "'
            }
        }
	},
	submit: {
		margin: theme.spacing(3, 0, 2)
	},
    link: {
        color: '#64b5f6',
        '&:hover': {
            color: '#53a3df'
        }
    }
}));

const SignUp = () => {
	const { paper, avatar, form, submit, link } = useStyles();

    const { register, handleSubmit, getValues, setError, formState: { errors } } = useForm();

    const { enqueueSnackbar } = useSnackbar();

    const [hasCompany, setHasCompany] = useState(false);

    const onSubmit = async data => {
        const response = await signUpUser(data);
        if (response.status === 422) {
            return response.data.violations.forEach(violation => {
                setError(violation.propertyPath, {
                    type: 'manual',
                    message: violation.message
                });
            });
        }
        enqueueSnackbar(
            'Uspešno ste se registrovali', {
            variant: 'success'
        });
        return history.push(REACT_ROUTES.HOME);
    };
	return (
		<Container component="main" maxWidth="xs">
			<CssBaseline />
			<Grid className={paper}>
				<Avatar className={avatar}>
					<LockOutlinedIcon />
				</Avatar>
				<Typography component="h1" variant="h5">
					Registracija
				</Typography>
				<form onSubmit={handleSubmit(onSubmit)} className={form}>
                    <TextField
						variant="standard"
						margin="normal"
						fullWidth
						name="firstName"
						label="Ime"
						autoComplete="firstName"
						autoFocus
                        {...register("firstName", { required: "Unesite ime" }) }
					/>
                    <ErrorMessage errors={errors} name="firstName" as="p" />
                    <TextField
						variant="standard"
						margin="normal"
						fullWidth
						name="lastName"
						label="Prezime"
						autoComplete="lastName"
                        {...register("lastName", { required: "Unesite prezime" }) }
					/>
                    <ErrorMessage errors={errors} name="lastName" as="p" />
					<TextField
						variant="standard"
						margin="normal"
						required
						fullWidth
						name="email"
						label="Email"
						autoComplete="email"
                        {...register("email", { required: "Unesite email" }) }
					/>
                    <ErrorMessage errors={errors} name="email" as="p" />
					<TextField
						variant="standard"
						margin="normal"
						fullWidth
						name="password"
						label="Lozinka"
						type="password"
						autoComplete="password"
                        {...register("password", {
                            required: "Unesite lozinku",
                            minLength: {
                                value: 6,
                                message: "Lozinka mora imati najmanje 6 karaktera"
                            }
                        })}
					/>
                    <ErrorMessage errors={errors} name="password" as="p" />
                    <TextField
						variant="standard"
						margin="normal"
						fullWidth
						name="confirmPassword"
						label="Lozinka"
						type="password"
						autoComplete="confirmPassword"
                        {...register("confirmPassword", {
                            required: "Potvrdite lozinku",
                            validate: value => (
                                value === getValues('password') || 'Lozinke se moraju poklapati'
                            )
                        })}
					/>
                    <FormControlLabel
						control={<Checkbox color="primary" value={hasCompany} onChange={(e) => setHasCompany(e.target.checked)} />}
						label="Imate kompaniju?"
					/>
                    {hasCompany &&
                        <>
                            <TextField
                                variant="standard"
                                margin="normal"
                                fullWidth
                                name="company"
                                label="Kompanija"
                                {...register("company", {
                                    validate: value => (
                                        (hasCompany && !!value) || 'Unesite ime kompanije'
                                    )
                                })}
                            />
                            <ErrorMessage errors={errors} name="company" as="p" />
                            <TextField
                                variant="standard"
                                margin="normal"
                                fullWidth
                                name="pib"
                                label="PIB"
                                {...register("pib", {
                                    validate: value => (
                                        (hasCompany && !!value) || 'Unesite PIB kompanije'
                                    )
                                })}
                            />
                            <ErrorMessage errors={errors} name="pib" as="p" />
                        </>
                    }
					<FormControlLabel
						control={<Checkbox value="remember" color="primary" />}
						label="Zapamti me"
					/>
					<Button
						type="submit"
						fullWidth
						variant="contained"
						color="primary"
						className={submit}
					>
						Registracija
					</Button>
					<Grid container justify="center">
						<Grid item>
							<Link className={link} to={REACT_ROUTES.HOME} variant="body2" color="primary">
                                Prijavite se
							</Link>
						</Grid>
					</Grid>
				</form>
			</Grid>
			<Box mt={8}>
				<Copyright />
			</Box>
		</Container>
	);
}

export default SignUp;
